import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { ApiNodeDataSelfie, FaceDetectionMode, SelfieOutputKind } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Tooltip } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import OutputMappings from '../../../shared/output-mappings';
import type { FormData } from './selfie.types';
type SelfieFormProps = {
  node: NodeProps<ApiNodeDataSelfie>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
  isFirmEmployee: boolean;
};

/** The selfie result is always emitted, so its row is always shown and can't be removed. */
const PINNED_OUTPUT_KINDS: SelfieOutputKind[] = ['result'];
const SelfieForm = ({
  node: {
    id,
    data: {
      requireFaceDetection,
      allowMultipleFaces,
      comparison,
      outputs
    }
  },
  mutation,
  canEdit,
  isFirmEmployee
}: SelfieFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'selfie'
  });
  const {
    toggle
  } = useDirty();
  const requirementOptions: {
    value: FaceDetectionMode;
    label: string;
  }[] = [{
    value: 'off',
    label: t($ => $.faceDetection.requirement.off)
  }, {
    value: 'best_effort',
    label: t($_0 => $_0.faceDetection.requirement.bestEffort)
  }, {
    value: 'required',
    label: t($_1 => $_1.faceDetection.requirement.required)
  }];
  const requirementHints: Record<FaceDetectionMode, string> = {
    off: t($_2 => $_2.faceDetection.requirement.hints.off),
    best_effort: t($_3 => $_3.faceDetection.requirement.hints.bestEffort),
    required: t($_4 => $_4.faceDetection.requirement.hints.required)
  };
  const outputKinds: {
    value: SelfieOutputKind;
    label: string;
  }[] = [{
    value: 'result',
    label: t($_5 => $_5.outputs.kinds.result)
  }];
  const form = useForm<FormData>({
    disabled: !canEdit,
    defaultValues: {
      requireFaceDetection,
      blockMultipleFaces: allowMultipleFaces === 'blocked',
      outputs: PINNED_OUTPUT_KINDS.map(kind => ({
        fieldKey: kind,
        onboardingDataKey: outputs.find(output => output.kind === kind)?.onboardingDataKey ?? ''
      }))
    }
  });
  const {
    handleSubmit,
    formState
  } = form;
  const requirement = useWatch({
    control: form.control,
    name: 'requireFaceDetection'
  });
  useEffect(() => {
    toggle('selfie-form', formState.isDirty);
  }, [formState.isDirty]);
  const handleFormSubmit = async (data: FormData) => {
    try {
      await mutation.mutateAsync({
        kind: 'update_node',
        nodeId: id,
        nodeData: {
          kind: 'selfie',
          requireFaceDetection: data.requireFaceDetection,
          // No detection => nothing to count faces against, so multiple-faces blocking is moot.
          allowMultipleFaces: data.requireFaceDetection !== 'off' && data.blockMultipleFaces ? 'blocked' : 'allowed',
          comparison,
          outputs: data.outputs.map(({
            fieldKey,
            onboardingDataKey
          }) => ({
            kind: fieldKey as SelfieOutputKind,
            onboardingDataKey
          }))
        }
      });
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  return <FormProvider {...form}>
      <form id="selfie-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          {isFirmEmployee && <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-label-2 text-primary">{t($_6 => $_6.faceDetection.requirement.label)}</span>
                  <Tooltip content={t($_7 => $_7.faceDetection.employeeOnly)} side="left">
                    <div className="flex-shrink-0 text-tertiary">
                      <IcoEyeCrossed16 />
                    </div>
                  </Tooltip>
                </div>
                <span className="text-body-2 text-secondary">{t($_8 => $_8.faceDetection.requirement.subtitle)}</span>
              </div>
              <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">
                <Form.Select<FormData> name="requireFaceDetection" aria-label={t($_9 => $_9.faceDetection.requirement.label)} hint={requirementHints[requirement]} options={requirementOptions} size="compact" />
                {requirement !== 'off' && <>
                    <Divider />
                    <Form.Toggle<FormData> name="blockMultipleFaces" label={t($_10 => $_10.faceDetection.blockMultipleFaces)} hint={t($_11 => $_11.faceDetection.blockMultipleFacesHint)} size="compact" />
                  </>}
              </div>
            </section>}
          <OutputMappings canEdit={canEdit} subtitle={t($_12 => $_12.outputs.subtitle)} options={outputKinds} outputLabel={t($_13 => $_13.outputs.outputLabel)} pinnedFieldKeys={PINNED_OUTPUT_KINDS} />
        </div>
      </form>
    </FormProvider>;
};
export default SelfieForm;